/**
 * Loading state for the lesson sequencing page.
 *
 * Mirrors the real layout rather than centring a spinner: the welcome header
 * with the auto-sequencing card, the group picker and a three-day roster all
 * land in the same places they will occupy, so the page does not jump when the
 * data arrives.
 *
 * The header reuses the page's own `et-welcome` / `et-sequencing-switch`
 * classes, so its spacing and paper card cannot drift from the real one.
 */

const DAYS = 3;
const ROWS = 5;

const Bar = ({ className }: { className: string }) => (
  <div className={`rounded-full ${className}`} />
);

export const LessonSequencingSkeleton = () => (
  <div
    className="flex animate-pulse flex-col gap-6 font-mulish"
    role="status"
    aria-label="Loading lesson sequencing"
  >
    {/* Header: title and strapline left, the auto-sequencing card right. It
        sits on the linen page, so its bars are a shade darker than the ones
        on paper below. */}
    <div className="et-welcome et-sequencing-welcome">
      <div className="flex flex-col gap-4">
        <Bar className="h-9 w-72 bg-[#E4DDCD] max-md:w-48" />
        <Bar className="h-4 w-80 bg-[#EDE8DC] max-md:w-56" />
      </div>
      <div className="et-sequencing-switch flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between gap-5">
          <div className="flex flex-col gap-2">
            <Bar className="h-4 w-28 bg-[#EDE8DC]" />
            <Bar className="h-3 w-16 bg-[#F3EDE2]" />
          </div>
          <div className="h-8 w-14 shrink-0 rounded-full bg-[#EDE8DC]" />
        </div>
        <div className="mt-1 border-t border-[#DED8CC] pt-3">
          <Bar className="h-4 w-32 bg-[#F3EDE2]" />
        </div>
      </div>
    </div>

    <section className="overflow-hidden rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1]">
      {/* Group picker row. */}
      <div className="border-b border-[#DED8CC] p-10 max-md:p-6">
        <Bar className="h-4 w-28 bg-[#EDE8DC]" />
        <div className="mt-4 h-11 w-full rounded-[6px] bg-[#F3EDE2]" />
      </div>

      {/* Students toolbar: heading and search. */}
      <div className="flex items-center justify-between gap-4 bg-[#FBF8F1] px-6 py-5">
        <div className="flex flex-col gap-2">
          <Bar className="h-5 w-24 bg-[#EDE8DC]" />
          <Bar className="h-3 w-36 bg-[#F3EDE2]" />
        </div>
        <div className="h-11 w-64 rounded-[6px] bg-[#F3EDE2] max-md:w-32" />
      </div>

      {/* Day headers, then one row per student. */}
      <div className="px-6 pb-6">
        <div className="flex items-center gap-4 py-4">
          {/* Same w-40 as the rows below, or the day columns start at a
              different x in the header than in the body. */}
          <div className="w-40 shrink-0">
            <Bar className="h-3 w-20 bg-[#F3EDE2]" />
          </div>
          <div className="flex flex-1 gap-4">
            {Array.from({ length: DAYS }, (_, day) => (
              <div
                key={day}
                className="h-14 flex-1 rounded-[6px] bg-[#F3EDE2]"
              />
            ))}
          </div>
        </div>
        {Array.from({ length: ROWS }, (_, row) => (
          <div
            key={row}
            className="flex items-center gap-4 border-t border-[#DED8CC] py-4"
          >
            <div className="flex w-40 shrink-0 items-center gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-[#EDE8DC]" />
              <div className="flex min-w-0 flex-col gap-1.5">
                <Bar className="h-3 w-20 bg-[#EDE8DC]" />
                <Bar className="h-2.5 w-14 bg-[#F3EDE2]" />
              </div>
            </div>
            <div className="flex flex-1 gap-4">
              {Array.from({ length: DAYS }, (_, day) => (
                <div
                  key={day}
                  className="h-16 flex-1 rounded-[6px] bg-[#F3EDE2]"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

export default LessonSequencingSkeleton;
